import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PortalLayout from "@/components/PortalLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Camera, ArrowLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const PALETTE = [
  "#D97706", "#0284C7", "#059669", "#7C3AED",
  "#DC2626", "#EA580C", "#0891B2", "#65A30D",
];

export default function PortalProfile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    avatar_color: user?.avatar_color || PALETTE[0],
    avatar_image: user?.avatar_image || "",
  });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [emailNotif, setEmailNotif] = useState(user?.email_notifications_enabled || false);

  const avatarSrc = form.avatar_image || null;

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Photo must be under 2 MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setForm((f) => ({ ...f, avatar_image: ev.target.result }));
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const r = await api.put("/auth/profile", {
        name: form.name,
        email: form.email,
        avatar_color: form.avatar_color,
        avatar_image: form.avatar_image,
      });
      const updated = r.data;
      setUser(updated);
      localStorage.setItem("user", JSON.stringify(updated));
      toast.success("Profile saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async () => {
    if (pwForm.new_password !== pwForm.confirm) { toast.error("Passwords don't match"); return; }
    if (pwForm.new_password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSavingPw(true);
    try {
      await api.put("/auth/profile", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      setPwForm({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password changed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setSavingPw(false);
    }
  };

  const roleLabel = user?.role === "client_lead" ? "Lead" : "Member";

  return (
    <PortalLayout>
      <div className="max-w-xl" data-testid="portal-profile-page">
        <div className="flex items-center gap-3 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded-lg hover:bg-stone-100"
            style={{ color: "#A8A29E" }}
          >
            <ArrowLeft size={18} strokeWidth={1.5} />
          </button>
          <div>
            <h1 className="font-serif text-3xl font-bold" style={{ color: "#292524" }}>Your Profile</h1>
            <p className="text-sm mt-0.5" style={{ color: "#57534E" }}>Manage your personal details</p>
          </div>
        </div>

        {/* Avatar */}
        <div className="bg-white rounded-xl border p-6 mb-4" style={{ borderColor: "#E7E5E4" }}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: "#A8A29E" }}>Avatar</p>
          <div className="flex items-start gap-6">
            <div className="relative flex-shrink-0">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold overflow-hidden"
                style={{ background: avatarSrc ? "transparent" : form.avatar_color }}
              >
                {avatarSrc
                  ? <img src={avatarSrc} alt="avatar" className="w-full h-full object-cover" />
                  : form.name?.[0]?.toUpperCase()}
              </div>
              <button
                data-testid="upload-photo-button"
                onClick={() => fileRef.current?.click()}
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full border-2 border-white flex items-center justify-center"
                style={{ background: "#292524" }}
              >
                <Camera size={12} style={{ color: "#fff" }} />
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
            </div>
            <div className="flex-1">
              <p className="text-xs mb-2" style={{ color: "#57534E" }}>Background colour</p>
              <div className="flex flex-wrap gap-2">
                {PALETTE.map((c) => (
                  <button
                    key={c}
                    data-testid={`color-${c.replace("#", "")}`}
                    onClick={() => setForm((f) => ({ ...f, avatar_color: c }))}
                    className="w-8 h-8 rounded-full transition-transform hover:scale-110 flex items-center justify-center"
                    style={{ background: c }}
                  >
                    {form.avatar_color === c && <Check size={12} style={{ color: "#fff" }} strokeWidth={3} />}
                  </button>
                ))}
                {avatarSrc && (
                  <button
                    data-testid="remove-photo-button"
                    onClick={() => setForm((f) => ({ ...f, avatar_image: "" }))}
                    className="text-xs px-2 py-1 rounded border"
                    style={{ borderColor: "#E7E5E4", color: "#78716C" }}
                  >
                    Remove photo
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Personal details */}
        <div className="bg-white rounded-xl border p-6 mb-4 space-y-4" style={{ borderColor: "#E7E5E4" }}>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Personal Details</p>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#57534E" }}>Full Name</Label>
            <Input
              data-testid="profile-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#57534E" }}>Email Address</Label>
            <Input
              data-testid="profile-email-input"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1.5"
            />
          </div>
          <div className="flex items-center gap-2 text-xs" style={{ color: "#A8A29E" }}>
            <span
              className="capitalize px-2 py-0.5 rounded-full font-medium"
              style={{ background: "#FEF3C7", color: "#D97706" }}
            >
              {roleLabel}
            </span>
            <span>role</span>
          </div>
          <Button
            data-testid="save-profile-button"
            onClick={saveProfile}
            disabled={saving}
            style={{ background: "#292524", color: "#fff" }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>

        {/* Password */}
        <div className="bg-white rounded-xl border p-6 space-y-4" style={{ borderColor: "#E7E5E4" }}>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Change Password</p>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#57534E" }}>Current Password</Label>
            <Input
              data-testid="current-password-input"
              type="password"
              value={pwForm.current_password}
              onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#57534E" }}>New Password</Label>
            <Input
              data-testid="new-password-input"
              type="password"
              value={pwForm.new_password}
              onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#57534E" }}>Confirm New Password</Label>
            <Input
              data-testid="confirm-password-input"
              type="password"
              value={pwForm.confirm}
              onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
              className="mt-1.5"
            />
          </div>
          <Button
            data-testid="save-password-button"
            onClick={savePassword}
            disabled={savingPw || !pwForm.current_password || !pwForm.new_password}
            variant="outline"
          >
            {savingPw ? "Updating..." : "Update Password"}
          </Button>
        </div>

        {/* Notification Preferences */}
        <div className="bg-white rounded-xl border p-6 space-y-4" style={{ borderColor: "#E7E5E4" }}>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Notification Preferences</p>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium" style={{ color: "#292524" }}>Email Notifications</p>
              <p className="text-xs mt-0.5" style={{ color: "#78716C" }}>
                Receive an email when Balbis sends you a new message
              </p>
            </div>
            <Switch
              data-testid="email-notifications-toggle"
              checked={emailNotif}
              onCheckedChange={async (val) => {
                setEmailNotif(val);
                await api.put("/auth/profile", { email_notifications_enabled: val });
                toast.success(val ? "Email notifications enabled" : "Email notifications disabled");
              }}
            />
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
